import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LogIn, Mail, Lock } from 'lucide-react';
import { loginSchema, type LoginInput } from '@marketing-os/shared';
import { applyApiErrors } from '@/lib/form-errors';
import { isClientPortalUser, useAuth } from '@/stores/auth.store';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { useLogin } from './api';

export function LoginPage() {
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [formError, setFormError] = useState<string | null>(null);
  const login = useLogin();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  if (isAuthenticated) {
    const next = searchParams.get('next');
    const fallback = isClientPortalUser(user) ? '/portal' : '/';
    return <Navigate to={next && next.startsWith('/') ? next : fallback} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login.mutateAsync(values);
    } catch (error) {
      setFormError(applyApiErrors(error, setError, ['email', 'password']));
    }
  });

  return (
    <div className="flex min-h-full items-center justify-center bg-bg px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-fg"
            >
              MO
            </span>
            <div>
              <p className="text-base font-semibold text-fg">Marketing OS</p>
              <p className="text-xs text-muted">Ícone Studio</p>
            </div>
          </div>
          <ThemeToggle />
        </div>

        <div className="surface-card p-6">
          <h1 className="text-lg font-semibold text-fg">Entrar na plataforma</h1>
          <p className="mt-1 text-sm text-muted">
            Use as credenciais fornecidas pelo administrador da agência.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            {formError && <Alert tone="danger">{formError}</Alert>}

            <Field label="E-mail" error={errors.email?.message} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="email"
                  autoComplete="email"
                  placeholder="voce@agencia.com.br"
                  leftIcon={<Mail className="h-4 w-4" />}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  {...register('email')}
                />
              )}
            </Field>

            <Field label="Senha" error={errors.password?.message} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  leftIcon={<Lock className="h-4 w-4" />}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  {...register('password')}
                />
              )}
            </Field>

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={isSubmitting || login.isPending}
              leftIcon={<LogIn className="h-4 w-4" />}
            >
              Entrar
            </Button>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-muted">
          Problemas de acesso? Fale com o administrador da sua organização.
        </p>
      </div>
    </div>
  );
}
